import {
  Controller,
  get,
  useFormContext,
  useFormState,
  useWatch
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { DayType } from 'generated/index';

import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel
} from '@mui/material';

import { IconButton } from '@sdb/planning-ui';
import { ControlledCheckbox, ControlledTextField } from 'components/shared';
import ControlledTimePicker from 'components/shared/ControlledFields/ControlledTimePicker';
import WeekdaysPicker from 'components/shared/WeekdaysPicker/WeekdaysPicker';

import { DAY_TYPES, WEEKDAYS } from '../../OpeningHours.constants';
import {
  DEFAULT_END_TIME,
  DEFAULT_START_TIME,
  toTime
} from '../../utils/openingScheduleForm';
import type {
  OpeningBlockFormValues,
  OpeningScheduleFormValues
} from '../../OpeningHours.types';
import {
  StyledBlock,
  StyledBlockRow,
  StyledDayTypes,
  StyledDescriptionField,
  StyledTimeFields
} from './OpeningBlockFields.styles';
import type { OpeningBlockFieldsProps } from './OpeningBlockFields.types';

const REFERENCE_START = toTime(DEFAULT_START_TIME)!;
const REFERENCE_END = toTime(DEFAULT_END_TIME)!;

/**
 * One line: a set of weekdays with one time slot, or "closed on these days", for the day types
 * ticked. A day type is typically shared by several lines (VSO and NSO), and one line can serve
 * several day types (a study day and a holiday often open alike).
 */
const OpeningBlockFields = ({
  blockIndex,
  canRemove,
  onRemove
}: OpeningBlockFieldsProps) => {
  'use memo';
  const { t } = useTranslation('common', {
    keyPrefix: 'planUnitHome.openingHours'
  });
  const { control, setValue, trigger } =
    useFormContext<OpeningScheduleFormValues>();

  const path = `blocks.${blockIndex}` as const;
  const block = useWatch({ control, name: path }) as OpeningBlockFormValues;
  // Its own subscription: the form's formState only re-renders the form, and the compiler keeps
  // this line from re-rendering along with it.
  const { errors, isSubmitted } = useFormState({ control, name: path });
  const blockError = get(errors, path);
  const dayTypesError = get(errors, `${path}.dayTypes`);

  // Only the day that changed is set, and the form is validated once afterwards. The "choose at
  // least one day" error sits on the line, not on a weekday field, and trigger() with the line's
  // path does not clear an error of the line itself, so the whole form is validated: one resolver
  // run instead of one per weekday.
  const onDaysChange = (days: Record<string, boolean>) => {
    WEEKDAYS.filter((weekday) => days[weekday] !== block[weekday]).forEach(
      (weekday) =>
        setValue(`${path}.${weekday}`, days[weekday], { shouldDirty: true })
    );
    if (isSubmitted) {
      trigger();
    }
  };

  return (
    <StyledBlock data-testid="opening-block">
      <StyledBlockRow>
        <WeekdaysPicker
          value={{
            monday: block.monday,
            tuesday: block.tuesday,
            wednesday: block.wednesday,
            thursday: block.thursday,
            friday: block.friday,
            saturday: block.saturday,
            sunday: block.sunday
          }}
          onChange={onDaysChange}
        />
        <ControlledCheckbox
          control={control}
          name={`${path}.closed`}
          label={t('closed')}
        />
        <StyledTimeFields>
          <ControlledTimePicker
            control={control}
            name={`${path}.startTime`}
            label={t('startTime')}
            disabled={block.closed}
            referenceDate={REFERENCE_START}
          />
          <ControlledTimePicker
            control={control}
            name={`${path}.endTime`}
            label={t('endTime')}
            disabled={block.closed}
            referenceDate={REFERENCE_END}
          />
        </StyledTimeFields>
        <StyledDescriptionField>
          <ControlledTextField
            control={control}
            name={`${path}.description`}
            label={t('blockDescription')}
            size="small"
            fullWidth
          />
        </StyledDescriptionField>
        <IconButton
          type="trash-can"
          color="secondary"
          aria-label={t('removeBlock')}
          onClick={onRemove}
          disabled={!canRemove}
        />
      </StyledBlockRow>

      <Controller
        control={control}
        name={`${path}.dayTypes`}
        render={({ field }) => {
          const toggle = (dayType: DayType, checked: boolean) =>
            field.onChange(
              checked
                ? [...field.value, dayType]
                : field.value.filter((value) => value !== dayType)
            );
          return (
            <FormControl error={!!dayTypesError} component="fieldset">
              <FormLabel component="legend">{t('appliesTo')}</FormLabel>
              <StyledDayTypes row>
                {DAY_TYPES.map((dayType) => (
                  <FormControlLabel
                    key={dayType}
                    label={t(`dayType.${dayType}`)}
                    control={
                      <Checkbox
                        checked={field.value.includes(dayType)}
                        onChange={(event) =>
                          toggle(dayType, event.target.checked)
                        }
                      />
                    }
                  />
                ))}
              </StyledDayTypes>
              {dayTypesError?.message && (
                <FormHelperText>{dayTypesError.message}</FormHelperText>
              )}
            </FormControl>
          );
        }}
      />

      {blockError?.message && (
        <FormHelperText error>{blockError.message}</FormHelperText>
      )}
    </StyledBlock>
  );
};

export default OpeningBlockFields;
